import { useNavigate, useParams } from 'react-router-dom';

import { observer } from 'mobx-react-lite';

import { Breadcrumb } from '@components/breadcrumb';
import { ButtonsNextPrevious } from '@components/buttons-next-previous';
import { IconButton } from '@components/icon-button';
import { IconCloseRegular } from '@components/icons/close-regular';
import { IconEdit } from '@components/icons/edit';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconSend } from '@components/icons/send';
import { IconTrash } from '@components/icons/trash';
import { Menu } from '@components/menu';
import { Stack } from '@components/stack';
import { storage } from '@utils/browser';

import { Header } from 'components/headers';
import { PageContent } from 'components/Page';
import { useAuthenticatedUser } from 'features/authentication';
import { useModal } from 'features/modals';
import { usePermissions } from 'features/permissions';
import { useStore } from 'models/Provider';
import { IUser } from 'models/types';

import { TeamDetailsProfile } from './team-details-profile';
import { getUserTabs } from './TeamList';

export const TeamDetails = observer(() => {
  const { userStore } = useStore();
  const { id } = useParams();
  const user: IUser = userStore.items.get(id);
  return (
    <>
      <Header
        mobileTitle={user.name}
        breadcrumbs={<TeamDetailCrumbs user={user} />}
        actions={<HeaderActions user={user} />}
      />
      <PageContent>
        {/* @ts-ignore */}
        <TeamDetailsProfile user={user} />
      </PageContent>
    </>
  );
});

export default TeamDetails;

const TeamDetailCrumbs = observer((props: { user: IUser }) => {
  const { user } = props;
  const { t } = useStore();

  let items = [{ label: t('navigation.settings.team'), link: '/settings/team' }];
  if (user) {
    items.push({ label: user.name, link: '' });
  }

  return <Breadcrumb items={items} />;
});

const HeaderActions = (props: { user: IUser }) => {
  const { user } = props;
  const { userStore, t } = useStore();
  const navigate = useNavigate();
  const { hideModal, showModal } = useModal();
  const { hasRolePermission } = usePermissions();
  const me = useAuthenticatedUser();

  const storedTabIndex = storage.get('team-tab');

  const userList: IUser[] = storedTabIndex
    ? Array.from(getUserTabs(userStore, t)[parseInt(storedTabIndex)].list)
    : Array.from(userStore.sorted);

  const currentIndex = userList.findIndex((u) => u.id === user.id);
  const prevIndex =
    currentIndex === 0 || userList.length === 1 ? userList.length - 1 : currentIndex - 1;
  const nextIndex = (currentIndex + 1) % userList.length;
  const prevUser = userStore.items.get(userList[prevIndex]?.id);
  const nextUser = userStore.items.get(userList[nextIndex]?.id);

  function deleteUserModal(user: IUser) {
    showModal('COMMON_REMOVE_CONFIRM', {
      _container: {
        width: '400px',
      },
      data: { id: user.id, confirmText: t('modals.removeconfirm.user.confirm') },
      confirm: async () => {
        await user.delete();
        hideModal();
        navigate('/settings/team');
      },
    });
  }

  function editUserModal(user: IUser) {
    // @ts-ignore
    showModal('EDIT_USER', {
      _container: {
        closeOnOverlayClickOrEsc: false,
        width: '460px',
      },
      user,
    });
  }

  function sendInviteModal(user: IUser) {
    // @ts-ignore
    showModal('CONFIRM_MODAL', {
      _container: {
        width: '460px',
      },
      confirm: user.sendInvite,
      data: { id: user.id },
      buttonIcon: <IconSend />,
      headerText: t('modals.sendinvite.header'),
      mainText: t('modals.sendinvite.main', user.name),
      confirmText: t('modals.sendinvite.button'),
    });
  }
  return (
    <>
      <Stack space={6} orientation="horizontal">
        <Stack space={2} orientation="horizontal">
          {userList.length > 1 && (
            <ButtonsNextPrevious
              onPrevious={() => navigate(prevUser.profileUrl)}
              onNext={() => navigate(nextUser.profileUrl)}
            />
          )}
          {hasRolePermission('team:edit-user') ? (
            <Menu
              buttonProps={{
                iconLeft: <IconMoreVertical />,
                variant: 'secondary',
              }}
              placement="left-start"
            >
              {user.id !== me.id ? (
                <Menu.Item
                  onSelect={() => editUserModal(user)}
                  icon={<IconEdit />}
                  label={t('user.options.edit')}
                />
              ) : (
                <Menu.Item
                  onSelect={() => navigate('/profile/details')}
                  icon={<IconEdit />}
                  label={t('user.options.edit')}
                />
              )}
              {!user.hasSetPassword ? (
                <Menu.Item
                  onSelect={() => sendInviteModal(user)}
                  icon={<IconSend />}
                  label={t('user.options.invite')}
                />
              ) : null}
              <Menu.Item
                onSelect={() => deleteUserModal(user)}
                icon={<IconTrash />}
                variant="danger"
                label={t('user.options.delete')}
              />
            </Menu>
          ) : null}
        </Stack>
        <Stack space={0}>
          <IconButton icon={<IconCloseRegular />} onClick={() => navigate('/settings/team')} />
        </Stack>
      </Stack>
    </>
  );
};
